import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getBidderId } from '@/redux/modules/account/user/user.selectors';
import {
    getIsItemSummaryTimedPlusBiddingOpen,
    getItemSummaryCatalogId,
    getItemSummaryCatalogOnly,
    getItemSummaryCatalogStatus,
    getItemSummaryIsPassed,
    getItemSummaryIsPaused,
    getItemSummaryIsSold,
    getItemSummarySellerId,
    getItemSummarySellerName,
    getItemSummaryStartTime,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemDataLeadingBidder } from '@/redux/modules/console';
import {
    getRegistrationDeclined,
    getRegistrationPending,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { openSendSellerMessageModal } from '@/redux/modules/modal';
import { shouldShowLive } from '@/utils/live';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback } from 'react';
import CardInfo from '../CardInfo/CardInfo';
import CardPrice from '../CardPrice/CardPrice';
import CardRegistration from '../CardRegistration/CardRegistration';
import ItemCardTitle from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import styled, { css } from 'styled-components';

export type CardGridBodyProps = {
    cardHovered: boolean;
    enterHoverState: () => void;
    enterLinkHoverState: () => void;
    isCurrentLiveitem: boolean;
    isMobile: boolean;
    isStoppedFromBidding: boolean;
    itemId: number;
    leaveHoverState: () => void;
    leaveLinkHoverState: () => void;
    linkHovered: boolean;
};

const CardGridBody = ({
    cardHovered,
    enterHoverState,
    enterLinkHoverState,
    isCurrentLiveitem,
    isMobile,
    isStoppedFromBidding,
    itemId,
    leaveHoverState,
    leaveLinkHoverState,
    linkHovered,
}: CardGridBodyProps) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryCatalogOnly(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const isPaused = useAppSelector((state) => getItemSummaryIsPaused(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemSummarySellerName(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummaryStartTime(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const isTimedPlusBiddingOpen = useAppSelector((state) => getIsItemSummaryTimedPlusBiddingOpen(state, itemId));

    const pending = useAppSelector((state) => getRegistrationPending(state, catalogId));
    const declined = useAppSelector((state) => getRegistrationDeclined(state, catalogId));

    const bidderId = useAppSelector(getBidderId);
    const leadingBidderId = useAppSelector((state) => getLiveItemDataLeadingBidder(state, itemId));

    const leadingBidder = leadingBidderId === bidderId;

    const showLive = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);

    const shouldIncreaseTitleContainerHeight = title.length < 30;
    const showLiveBidButton = showLive && !isStoppedFromBidding && !leadingBidder && isCurrentLiveitem;
    const showDeniedRegistration = declined && !isMobile && catalogStatus !== 'done' && !showLiveBidButton;
    const isOpenItem = !isPassed && !isSold && isTimedPlusBiddingOpen;

    const showSendSellerMessageModalDeclined = useCallback(() => {
        const messageDetails = {
            catalogId,
            message: `${formatMessage({
                id: 'message_registration_declined',
            })}`,
            title,
        };

        const subject = `${formatMessage({
            id: 'registration_declined',
        })}: ${title}`;

        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [catalogId, dispatch, formatMessage, sellerName, sellerId, title]);

    return (
        <PlacedDetailsForNewMobileGrid
            $denied={showDeniedRegistration}
            $pending={pending}
            $shouldIncreaseTitleContainerHeight={shouldIncreaseTitleContainerHeight}
            data-testid="placed-details-for-mobile"
        >
            <CardInfo
                isMobile={isMobile}
                isOpenItem={isOpenItem}
                itemId={itemId}
            />
            <PlacedItemCardTitle
                isHovered={linkHovered}
                itemId={itemId}
                onMouseEnter={enterLinkHoverState}
                onMouseLeave={leaveLinkHoverState}
                showLotNumber
                testid="itemCardTitle"
            />
            <CardPrice
                enterLinkHoverState={enterHoverState}
                isCardHovered={cardHovered}
                isLinkHovered={linkHovered}
                isMobile={isMobile}
                itemId={itemId}
                leaveLinkHoverState={leaveHoverState}
            />
            {!isSold && !isPassed && !isPaused && (
                <CardRegistration
                    isMobile={isMobile}
                    isOpenItem={isOpenItem}
                    itemId={itemId}
                />
            )}
            {showDeniedRegistration && (
                <ContactAuctioneerLink onClick={showSendSellerMessageModalDeclined}>
                    <FormattedMessage id="registration.message_auctioneer" />
                </ContactAuctioneerLink>
            )}
        </PlacedDetailsForNewMobileGrid>
    );
};

export default CardGridBody;

const PlacedItemCardTitle = styled(ItemCardTitle)`
    grid-area: title;
`;

const PlacedDetailsForNewMobileGrid = styled.section<{
    $denied: boolean;
    $pending: boolean;
    $shouldIncreaseTitleContainerHeight: boolean;
}>`
    display: grid;
    overflow: hidden;
    grid-area: alldetailsformobilelayout;
    grid-template-areas:
        '.'
        'iteminfo'
        '.'
        'title'
        '.'
        'currentbidprice'
        '.'
        'registerForAuction'
        '.'
        'bidButtonForLiveItem';
    grid-template-rows: 8px min-content 4px min-content 4px min-content 16px min-content;
    ${({ $shouldIncreaseTitleContainerHeight }) =>
        $shouldIncreaseTitleContainerHeight &&
        css`
            grid-template-rows: 8px min-content 4px 48px 4px min-content 16px min-content;
        `}

    ${({ $pending }) =>
        $pending &&
        css`
            grid-template-rows: 8px min-content 4px min-content 4px min-content 16px min-content;
        `}
            ${({ $denied }) =>
        $denied &&
        css`
            grid-template-areas:
                '.'
                'iteminfo'
                '.'
                'title'
                '.'
                'currentbidprice'
                '.'
                'registerForAuction'
                '.'
                'messageAuctioneerLink';
            grid-template-rows: 8px min-content 4px min-content 4px min-content 16px min-content 4px min-content;
        `}
        ${({ $denied, $shouldIncreaseTitleContainerHeight }) =>
        $denied &&
        $shouldIncreaseTitleContainerHeight &&
        css`
            grid-template-areas:
                '.'
                'iteminfo'
                '.'
                'title'
                '.'
                'currentbidprice'
                '.'
                'registerForAuction'
                '.'
                'messageAuctioneerLink';
            grid-template-rows: 8px min-content 4px 48px 4px min-content 16px min-content 4px min-content;
        `}

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        grid-template-rows: 17px 6px min-content 2px 20px 4px min-content 4px;
        grid-template-areas:
            'iteminfo'
            '.'
            'title'
            '.'
            'currentbidprice'
            '.'
            'registerForAuction'
            '.'
            'bidButtonForLiveItem';
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobileWidth}) {
        grid-template-rows: 22px 0 min-content 4px min-content;
    }
`;

const ContactAuctioneerLink = styled(Link)`
    grid-area: messageAuctioneerLink;
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;
